import { useForm } from "react-hook-form";
import { useNotification } from "../../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import { SignupForm } from "../../components/signup-form";
import { useTelegram } from "../../hooks/useTelegram";
import VerificationModal from "../../components/VerificationModal";
import { useState } from "react";
import TelegramLoginButton from "../../components/ui/TelegramLoginButton";
import { useAuth } from "../../context/AuthContext";
import TermsAcceptance, {
  allTermsAccepted,
  validateTermsAcceptance,
} from "../../components/legal/TermsAcceptance";

export default function Register() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: async (data) => {
      const errors = {};
      if (data.password !== data.confirmPassword) {
        errors.confirmPassword = { message: "Passwords do not match" };
      }
      if (data.password.length < 8) {
        errors.password = { message: "Password must be at least 8 characters" };
      }
      return { values: data, errors };
    },
  });

  const { add } = useNotification();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { user: telegramUser, initData, isTelegram } = useTelegram();
  const [showModal, setShowModal] = useState(false);
  const [termsValues, setTermsValues] = useState({
    acceptTerms: false,
    acceptPrivacy: false,
    acceptCookies: false,
  });
  const [termsErrors, setTermsErrors] = useState({});
  const [telegramTermsValues, setTelegramTermsValues] = useState({
    acceptTerms: false,
    acceptPrivacy: false,
    acceptCookies: false,
  });
  const [telegramTermsErrors, setTelegramTermsErrors] = useState({});

  const handleTermsChange = (key, checked) => {
    setTermsValues((prev) => ({ ...prev, [key]: checked }));
    setTermsErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleTelegramTermsChange = (key, checked) => {
    setTelegramTermsValues((prev) => ({ ...prev, [key]: checked }));
    setTelegramTermsErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const onSubmit = async (data) => {
    const policyErrors = validateTermsAcceptance(termsValues);
    if (Object.keys(policyErrors).length > 0) {
      setTermsErrors(policyErrors);
      return;
    }

    const { confirmPassword, ...payload } = data;

    try {
      await client.post("/auth/register", { ...payload, ...termsValues });
      if (data.role === "RECRUITER") {
        setShowModal(true);
      } else {
        add("Registration successful! Please check your email to verify.", "success");
        navigate("/login");
      }
    } catch (err) {
      add(err.response?.data?.message || "Registration failed", "error");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    navigate("/login");
  };

  const handleTelegramAuth = async (payload) => {
    const policyErrors = validateTermsAcceptance(telegramTermsValues);
    if (Object.keys(policyErrors).length > 0) {
      setTelegramTermsErrors(policyErrors);
      add("Please accept all policies before continuing with Telegram.", "error");
      return;
    }

    try {
      const res = await client.post("/auth/telegram", {
        ...payload,
        ...telegramTermsValues,
      });
      add("Registration successful!", "success");
      login(res.data.token, res.data.user, navigate);
    } catch (err) {
      add(
        err.response?.data?.message || "Telegram registration failed",
        "error",
      );
    }
  };

  const handleTelegramWebAppRegister = async () => {
    if (!telegramUser || !initData) {
      add("Telegram user data missing. Are you inside Telegram?", "error");
      return;
    }
    await handleTelegramAuth({ initData });
  };

  const handleWidgetRegister = async (user) => {
    await handleTelegramAuth({ widgetData: user });
  };

  return (
    <div className="min-h-screen ">
      <div className="grid min-h-screen lg:grid-cols-2">
        {/* LEFT SIDE – BRAND & FEATURES */}
        <div className="hidden lg:flex flex-col justify-center p-8 lg:p-12 ">
          <div className="max-w-lg mx-auto space-y-8">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                Start your journey with{" "}
                <span className="text-primary">Shebeka</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Join thousands of professionals finding their dream jobs.
              </p>
            </div>

            <ul className="space-y-4">
              {[
                ["Connect with top recruiters", "Access opportunities from leading companies"],
                ["Track your applications", "Monitor your progress in real-time"],
                ["Get your dream job", "Land the role you've been looking for"],
              ].map(([title, desc]) => (
                <li key={title} className="flex items-start gap-3">
                  <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                    <svg className="w-3 h-3 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">{title}</p>
                    <p className="text-sm text-muted-foreground">{desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* RIGHT SIDE – FORM */}
        <div className="flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md space-y-6">
            <SignupForm
              onSubmit={handleSubmit(onSubmit)}
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              termsValues={termsValues}
              onTermsChange={handleTermsChange}
              termsErrors={termsErrors}
            />

            {(isTelegram || !isTelegram) && (
              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className=" px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>

                <TermsAcceptance
                  values={telegramTermsValues}
                  onChange={handleTelegramTermsChange}
                  errors={telegramTermsErrors}
                />

                {isTelegram ? (
                  <button
                    onClick={handleTelegramWebAppRegister}
                    disabled={!allTermsAccepted(telegramTermsValues)}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                    </svg>
                    Register with Telegram
                  </button>
                ) : (
                  <TelegramLoginButton
                    botName="shebekabot"
                    onAuth={handleWidgetRegister}
                    disabled={!allTermsAccepted(telegramTermsValues)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <VerificationModal isOpen={showModal} onClose={handleCloseModal} />
    </div>
  );
}
